import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import clsx from "clsx";
import { AppShell } from "@/components/AppShell";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { ApiError } from "@/lib/apiClient";
import { listMaintenanceTypes, lookupAssets, startMaintenanceEvent } from "@/lib/wartungen";

interface AssetItem {
  asset_id?: number;
  external_id?: string | null;
  name?: string | null;
  status?: string | null;
  location_label?: string | null;
  [k: string]: unknown;
}

type Tone = "info" | "error" | "warning";

function num(params: URLSearchParams, key: string): number | null {
  const raw = params.get(key);
  if (raw == null || String(raw).trim() === "") return null;
  const parsed = Number(raw);
  return Number.isFinite(parsed) && Number.isInteger(parsed) && parsed > 0 ? parsed : null;
}

function extractMasterdataError(body: unknown): { isMasterdata: boolean; message?: string; eventId?: number | null } {
  if (!body || typeof body !== "object") return { isMasterdata: false };
  const obj = body as Record<string, unknown>;
  const detail =
    obj.detail && typeof obj.detail === "object" && !Array.isArray(obj.detail)
      ? (obj.detail as Record<string, unknown>)
      : obj;
  if (detail.error_code !== "MASTERDATA_REQUIRED_MISSING") return { isMasterdata: false };
  const missing = Array.isArray(detail.missing_fields)
    ? detail.missing_fields
        .map((e) => {
          if (typeof e === "string") return e.trim();
          if (e && typeof e === "object") {
            const r = e as Record<string, unknown>;
            return String(r.label || r.field_label || r.field_key || "").trim();
          }
          return String(e).trim();
        })
        .filter(Boolean)
    : [];
  const base =
    (typeof detail.message === "string" && detail.message.trim()) ||
    "Pflichtfelder in den Stammdaten fehlen. Bitte zuerst Stammdaten vervollständigen.";
  const message = missing.length ? `${base} Fehlende Felder: ${missing.join(", ")}.` : base;
  const eventCandidate = Number(detail.maintenance_event_id ?? detail.event_id);
  return {
    isMasterdata: true,
    message,
    eventId: Number.isFinite(eventCandidate) && eventCandidate > 0 ? eventCandidate : null,
  };
}

export function ObjektAuswahlPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const typeKey = (params.get("type") || "").trim();

  const projectId = num(params, "project_id");
  const contractId = num(params, "contract_id");
  const heroAddressId = num(params, "hero_address_id");
  const subLocationId = num(params, "sub_location_id");
  const restrictToContract = String(params.get("restrict_to_contract_assets") || "").trim() === "1";
  const prefilledExternalId = (params.get("external_id") || "").trim();

  const schedule = useMemo(() => {
    const dateMode = String(params.get("date_mode") || "day").trim().toLowerCase();
    const dateValue = String(params.get("date_value") || "").trim();
    if (["day", "month", "year"].includes(dateMode) && dateValue) return { dateMode, dateValue };
    return { dateMode: "day", dateValue: new Date().toISOString().slice(0, 10) };
  }, [params]);

  const [title, setTitle] = useState("Objekt auswählen");
  const [query, setQuery] = useState(prefilledExternalId);
  const [items, setItems] = useState<AssetItem[]>([]);
  const [hint, setHint] = useState("");
  const [selectedAssetId, setSelectedAssetId] = useState<number | null>(null);
  const [feedback, setFeedback] = useState<{ tone: Tone; message: string } | null>(null);
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    if (!typeKey) return;
    void (async () => {
      try {
        const list = await listMaintenanceTypes();
        const active = list.find((t) => String(t.key).trim() === typeKey);
        setTitle(`Objekt auswählen – ${active?.name || typeKey}`);
      } catch {
        /* ignore */
      }
    })();
  }, [typeKey]);

  const doLookup = useCallback(
    async (q: string, allowEmpty: boolean) => {
      if (!typeKey) return;
      const trimmed = q.trim();
      if (!trimmed && !allowEmpty) {
        setItems([]);
        setSelectedAssetId(null);
        setHint("Bitte Kennung eingeben, um passende Assets zu suchen.");
        setFeedback(null);
        return;
      }
      try {
        const result = await lookupAssets(typeKey, {
          q: trimmed || undefined,
          contract_id: contractId ?? undefined,
          hero_address_id: heroAddressId ?? undefined,
          sub_location_id: subLocationId ?? undefined,
          restrict_to_contract_assets: restrictToContract ? "1" : undefined,
        });
        const list = (Array.isArray(result.items) ? result.items : []) as AssetItem[];
        const count = Number(result.count ?? list.length ?? 0);
        setItems(list);

        // auto-select
        const preferred = num(params, "asset_id");
        if (preferred && list.some((a) => Number(a.asset_id) === preferred)) {
          setSelectedAssetId(preferred);
        } else if (result.mode === "exact" && list.length === 1) {
          setSelectedAssetId(Number(list[0]?.asset_id) || null);
        } else {
          setSelectedAssetId(null);
        }

        if (count === 0) {
          setHint("");
          setFeedback({
            tone: "warning",
            message: "Kein passendes Asset gefunden. Bitte Kennung prüfen. Objekt nicht vorhanden? Jetzt anlegen.",
          });
        } else if (count > 1) {
          setHint(`${count} Assets gefunden. Bitte genau ein Asset auswählen.`);
          setFeedback({ tone: "warning", message: "Mehrere Treffer gefunden. Bitte ein Asset explizit auswählen." });
        } else {
          setHint("1 Asset gefunden. Bitte Auswahl prüfen und starten.");
          setFeedback(null);
        }
      } catch {
        setFeedback({ tone: "error", message: "Assets konnten nicht gesucht werden. Bitte später erneut versuchen." });
      }
    },
    [typeKey, contractId, heroAddressId, subLocationId, restrictToContract, params],
  );

  // initial empty lookup if no prefilled external id
  const inited = useRef(false);
  useEffect(() => {
    if (inited.current || !typeKey) return;
    inited.current = true;
    if (prefilledExternalId) void doLookup(prefilledExternalId, false);
    else void doLookup("", true);
  }, [typeKey, prefilledExternalId, doLookup]);

  // debounced search on query change
  useEffect(() => {
    if (!typeKey || !inited.current) return;
    const handle = window.setTimeout(() => void doLookup(query, false), 250);
    return () => window.clearTimeout(handle);
  }, [query, typeKey, doLookup]);

  function createAssetUrl(): string {
    const usp = new URLSearchParams();
    if (typeKey) {
      usp.set("type", typeKey);
      usp.set("type_key", typeKey);
    }
    if (contractId) usp.set("contract_id", String(contractId));
    if (heroAddressId) usp.set("hero_address_id", String(heroAddressId));
    if (subLocationId) usp.set("sub_location_id", String(subLocationId));
    usp.set("after_create", "selection");
    if (query.trim()) usp.set("external_id", query.trim());
    return `/wartungen/objekt-anlegen?${usp.toString()}`;
  }

  async function startMaintenance() {
    if (!typeKey || !selectedAssetId) {
      setFeedback({ tone: "warning", message: "Bitte zuerst eindeutig ein Asset auswählen." });
      return;
    }
    setStarting(true);
    setFeedback(null);
    try {
      const result = await startMaintenanceEvent(typeKey, {
        asset_id: selectedAssetId,
        project_id: projectId,
        date_mode: schedule.dateMode,
        date_value: schedule.dateValue,
      });
      const eventId = Number(result?.maintenance_event_id);
      if (!Number.isFinite(eventId) || eventId <= 0) throw new Error("Ungültige maintenance_event_id.");
      const usp = new URLSearchParams();
      usp.set("type", typeKey);
      usp.set("asset_id", String(selectedAssetId));
      usp.set("maintenance_event_id", String(eventId));
      navigate(`/wartungen/form?${usp.toString()}`);
    } catch (err) {
      if (err instanceof ApiError && err.status === 422) {
        const md = extractMasterdataError(err.body);
        if (md.isMasterdata) {
          setFeedback({ tone: "warning", message: md.message || "Stammdaten unvollständig." });
          const usp = new URLSearchParams();
          usp.set("type", typeKey);
          usp.set("mode", "edit");
          usp.set("asset_id", String(selectedAssetId));
          usp.set("return_to", "form");
          if (md.eventId) usp.set("maintenance_event_id", String(md.eventId));
          navigate(`/wartungen/objekt-anlegen?${usp.toString()}`);
          return;
        }
      }
      setFeedback({ tone: "error", message: "Wartungsvorgang konnte nicht gestartet werden." });
      setStarting(false);
    }
  }

  if (!typeKey) {
    return (
      <AppShell title="Objekt auswählen" back={{ to: "/wartungen", label: "zurück zur Übersicht" }}>
        <Alert variant="warning">Bitte Seite über eine Wartungskategorie öffnen.</Alert>
      </AppShell>
    );
  }

  return (
    <AppShell
      title={title}
      subtitle={`Schlüssel: ${typeKey}`}
      back={{ to: `/wartungen/kategorie?kategorie=${encodeURIComponent(typeKey)}`, label: "zurück zur Kategorie" }}
      actions={
        <a
          href={createAssetUrl()}
          className="inline-flex h-10 items-center rounded-lg bg-card px-4 text-sm font-medium text-accent ring-1 ring-inset ring-separator-opaque hover:bg-fill-tertiary"
        >
          Objekt anlegen
        </a>
      }
    >
      {feedback ? (
        <Alert variant={feedback.tone === "error" ? "error" : feedback.tone === "warning" ? "warning" : "info"} className="mb-4">
          {feedback.message}
        </Alert>
      ) : null}

      <input
        className="eh-input mb-2 w-full max-w-md"
        placeholder="Objektkennung / External-ID eingeben …"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        autoFocus
      />
      {hint ? <p className="mb-3 text-xs text-label-secondary">{hint}</p> : null}

      <div className="overflow-x-auto rounded-ios border border-separator bg-card shadow-ios">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-separator text-left text-xs uppercase tracking-wide text-label-tertiary">
              <th className="px-3 py-2"></th>
              <th className="px-3 py-2 font-medium">External-ID</th>
              <th className="px-3 py-2 font-medium">Name</th>
              <th className="px-3 py-2 font-medium">Status</th>
              <th className="px-3 py-2 font-medium">Ort</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-3 py-3 text-label-secondary">
                  Keine passenden Assets gefunden.
                </td>
              </tr>
            ) : (
              items.map((item) => {
                const id = Number(item.asset_id);
                return (
                  <tr
                    key={id}
                    className={clsx(
                      "cursor-pointer border-b border-separator/60 hover:bg-fill-tertiary",
                      selectedAssetId === id && "bg-accent-soft",
                    )}
                    onClick={() => setSelectedAssetId(id)}
                  >
                    <td className="px-3 py-2">
                      <input
                        type="radio"
                        name="selected-asset"
                        checked={selectedAssetId === id}
                        onChange={() => setSelectedAssetId(id)}
                      />
                    </td>
                    <td className="px-3 py-2">{item.external_id || "–"}</td>
                    <td className="px-3 py-2">{item.name || "–"}</td>
                    <td className="px-3 py-2">{item.status || "–"}</td>
                    <td className="px-3 py-2">{item.location_label || "Basisort"}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-4">
        <Button type="button" disabled={!selectedAssetId || starting} onClick={startMaintenance}>
          {starting ? "Starte …" : "Wartung starten"}
        </Button>
      </div>
    </AppShell>
  );
}
